import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { Article } from '../../../../../types/article.types';
import { ArticleSelectionItem } from '../../../../../types/studio.types';
import { alpha } from '@mui/material/styles';
import ArticlePickerDialog from './ArticlePickerDialog';
import { getArticlesByWebsite } from '../../../../articles/api';

interface ArticleSelectionPanelProps {
  websitePK: string;
  selectedItems: ArticleSelectionItem[];
  onSelectionChange: (items: ArticleSelectionItem[]) => void;
  disabled?: boolean;
}

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  } catch {
    return dateString;
  }
};

const ArticleSelectionPanel: React.FC<ArticleSelectionPanelProps> = ({
  websitePK,
  selectedItems,
  onSelectionChange,
  disabled = false,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState<Article[]>([]);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  // Used to ignore stale async loads when websitePK/selectedItems change rapidly
  const loadSeqRef = useRef(0);

  // Load full article objects when selectedItems changes
  useEffect(() => {
    const loadArticles = async () => {
      const seq = ++loadSeqRef.current;

      if (!selectedItems || selectedItems.length === 0) {
        setSelectedArticles([]);
        return;
      }

      try {
        const data = await getArticlesByWebsite(websitePK);

        // If a newer request started, ignore this result
        if (seq !== loadSeqRef.current) return;

        // Handle response format: check if data has an 'articles' property, otherwise assume it's already an array
        const articlesArray = Array.isArray(data)
          ? data
          : ((data as any)?.articles && Array.isArray((data as any).articles) ? (data as any).articles : []);

        // Filter to only include the selected articles, maintaining the order from selectedItems
        const loadedArticles = selectedItems
          .map((item) => articlesArray.find((article: Article) => article.id === item.articleId))
          .filter((article): article is Article => article !== undefined);

        setSelectedArticles(loadedArticles);
      } catch (error) {
        console.error('Error loading selected articles:', error);
        setSelectedArticles([]);
      }
    };

    loadArticles();
  }, [selectedItems, websitePK]);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    if (!destination) return;
    if (destination.index === source.index) return;

    // IMPORTANT:
    // The draggable list is rendered from `selectedArticles`, which is loaded async.
    // If `selectedArticles` is temporarily missing an item (stale/partial load), its indices can diverge
    // from `selectedItems`. Reordering `selectedItems` by index can then move the wrong item, and once
    // the async load completes the UI snaps back (appearing like the reorder didn't "stick").
    //
    // So: reorder by the currently-rendered article id list, then map back to `selectedItems` by articleId.
    const renderedIds = selectedArticles.map((a) => a.id);
    if (renderedIds.length === 0) return;

    const ids = Array.from(renderedIds);
    const [movedId] = ids.splice(source.index, 1);
    ids.splice(destination.index, 0, movedId);

    const byId = new Map(selectedItems.map((it) => [it.articleId, it] as const));
    const reorderedByRender = ids
      .map((id) => byId.get(id))
      .filter((it): it is ArticleSelectionItem => !!it);

    // Preserve any selectedItems that are not currently rendered (e.g., article not found yet)
    // by appending them at the end (stable).
    const renderedSet = new Set(ids);
    const missing = selectedItems.filter((it) => !renderedSet.has(it.articleId));

    onSelectionChange([...reorderedByRender, ...missing]);
  };

  const handleDeleteArticle = (index: number) => {
    // Delete should also respect the currently rendered order to avoid index mismatch.
    const renderedIds = selectedArticles.map((a) => a.id);
    if (renderedIds.length === 0) return;

    const idToDelete = renderedIds[index];
    if (!idToDelete) return;

    const updated = selectedItems.filter((it) => it.articleId !== idToDelete);
    onSelectionChange(updated);
  };

  const handleAddArticles = (articles: Article[]) => {
    // Filter out articles that are already selected
    const newArticles = articles.filter(
      (article) => !selectedItems.some((selected) => selected.articleId === article.id)
    );
    const newItems: ArticleSelectionItem[] = newArticles.map((a) => ({ articleId: a.id }));
    const updated = [...selectedItems, ...newItems];
    onSelectionChange(updated);
    setPickerOpen(false);
  };

  const handleConfirmClearAll = () => {
    onSelectionChange([]);
    setConfirmClearOpen(false);
  };

  const handleUpdateItem = (index: number, updates: Partial<ArticleSelectionItem>) => {
    const updated = [...selectedItems];
    updated[index] = { ...updated[index], ...updates };
    onSelectionChange(updated);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">
          <strong>Articles in Podcast ({selectedArticles.length})</strong>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setPickerOpen(true)}
            disabled={disabled}
          >
            Add Articles
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmClearOpen(true)}
            disabled={disabled || selectedArticles.length === 0}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {selectedArticles.length === 0 ? (
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            bgcolor: 'background.default',
            borderStyle: 'dashed'
          }}
        >
          <Typography color="text.secondary">
            No articles selected. Click "Add Articles" to get started.
          </Typography>
        </Paper>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="articles-droppable" direction="vertical">
            {(dropProvided) => (
              <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                {selectedArticles.map((article, index) => {
                  const item = selectedItems.find(i => i.articleId === article.id) || { articleId: article.id };
                  return (
                  <Draggable 
                    key={article.id} 
                    draggableId={article.id} 
                    index={index}
                    isDragDisabled={disabled}
                  >
                    {(dragProvided, snapshot) => (
                      <Paper
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        elevation={snapshot.isDragging ? 3 : 0}
                        variant="outlined"
                        sx={{
                          mb: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          p: 1.5,
                          bgcolor: snapshot.isDragging 
                            ? (theme) => alpha(theme.palette.primary.main, 0.1)
                            : 'background.paper',
                          borderColor: snapshot.isDragging 
                            ? 'primary.main' 
                            : 'divider',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: 'primary.light',
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                          },
                        }}
                        style={{
                          ...(dragProvided.draggableProps.style as React.CSSProperties),
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          {/* Drag handle */}
                          <Box
                            {...dragProvided.dragHandleProps}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: disabled ? 'default' : (snapshot.isDragging ? 'grabbing' : 'grab'),
                              color: 'text.secondary',
                              mr: 1,
                            }}
                          >
                            <DragIndicatorIcon />
                          </Box>

                          {/* Article number badge */}
                          <Box
                            sx={{
                              minWidth: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '0.875rem',
                              mr: 1,
                            }}
                          >
                            {index + 1}
                          </Box>

                          {/* Article info */}
                          <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {article.headline}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                              {article.journalistName && (
                                <Typography variant="body2" color="text.secondary">
                                  {article.journalistName}
                                </Typography>
                              )}
                              {article.journalistName && article.publishDate && (
                                <Typography variant="body2" color="text.secondary">•</Typography>
                              )}
                              {article.publishDate && (
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(article.publishDate)}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Verbatim Settings */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mr: 4 }}>
                            <Tooltip title="Check this box to have the article read verbatim instead of being summarized.">
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={!!item.verbatim}
                                    onChange={(e) => handleUpdateItem(index, { verbatim: e.target.checked })}
                                    disabled={disabled}
                                  />
                                }
                                label={<Typography variant="body2">Verbatim</Typography>}
                                sx={{ mr: 0 }}
                              />
                            </Tooltip>
                            
                            {item.verbatim && (
                              <Tooltip title="Enter the Voice ID to be used for reading this article (e.g., 6PwnQFIMLnKYha5feGsR).">
                                <TextField
                                  size="small"
                                  placeholder="Voice ID"
                                  value={item.verbatimVoiceId || ''}
                                  onChange={(e) => handleUpdateItem(index, { verbatimVoiceId: e.target.value })}
                                  disabled={disabled}
                                  sx={{ width: 250 }}
                                  required={!!item.verbatim}
                                />
                              </Tooltip>
                            )}
                          </Box>

                          {/* Priority badge (optional) */}
                          {article.priority && (
                            <Chip
                              size="small"
                              label={
                                article.priority === 1 ? 'High' :
                                article.priority === 2 ? 'Medium' :
                                'Low'
                              }
                              color={
                                article.priority === 1 ? 'error' :
                                article.priority === 2 ? 'warning' :
                                'default'
                              }
                              sx={{ mr: 1 }}
                            />
                          )}

                          {/* Delete button */}
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteArticle(index)}
                            disabled={disabled}
                            aria-label="Remove article"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Paper>
                    )}
                  </Draggable>
                );
                })}
                {dropProvided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <Dialog
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        aria-labelledby="confirm-clear-dialog-title"
      >
        <DialogTitle id="confirm-clear-dialog-title">Remove All Articles</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove all selected articles from this podcast? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)} disabled={disabled}>Cancel</Button>
          <Button onClick={handleConfirmClearAll} color="error" variant="contained" disabled={disabled}>Remove All</Button>
        </DialogActions>
      </Dialog>

      {/* Article picker dialog */}
      <ArticlePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleAddArticles}
        websiteId={websitePK}
        excludeArticleIds={selectedItems.map(i => i.articleId)}
      />
    </Box>
  );
};

export default ArticleSelectionPanel;
